import type { ReactNode } from 'react';

import { cn } from '~/utils/cn';

import { opTone, type PillTone } from '../../utils/taxonomy-tones';

const TONE_CLASS: Record<PillTone, string> = {
  neutral: 'bg-muted text-muted-foreground border-border',
  primary: 'bg-primary/10 text-primary border-transparent',
  success: 'bg-success-50 text-success border-transparent',
  info: 'bg-info-50 text-info border-transparent',
  warning: 'bg-warning-50 text-warning border-transparent',
  error: 'bg-error-50 text-error border-transparent',
  violet: 'bg-replaced-50 text-replaced border-transparent',
};

/** Small rounded label used for ids, statuses, types and confidence across the studio. */
export function TaxPill({
  tone = 'neutral',
  mono = false,
  className,
  title,
  children,
}: {
  tone?: PillTone;
  mono?: boolean;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 rounded-full border px-2 text-[11px] font-medium whitespace-nowrap',
        mono && 'font-mono',
        TONE_CLASS[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const DOT_CLASS: Record<PillTone, string> = {
  neutral: 'bg-muted-foreground',
  primary: 'bg-primary',
  success: 'bg-success',
  info: 'bg-info',
  warning: 'bg-warning',
  error: 'bg-error',
  violet: 'bg-replaced',
};

/** Coloured dot marking an unpublished change (op) on a row. */
export function OpDot({ op, title }: { op: string; title?: string }) {
  return (
    <span
      title={title}
      aria-label={title}
      role="img"
      className={cn('inline-block h-2 w-2 shrink-0 rounded-full', DOT_CLASS[opTone(op)])}
    />
  );
}
